import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  FaChevronLeft,
  FaPlus,
  FaChevronUp,
  FaTimes,
  FaCalendarAlt,
} from "react-icons/fa";

/* ─── PHQ-9 Questions ─── */
const PHQ9_QUESTIONS = [
  "Little interest or pleasure in doing things",
  "Feeling down, depressed, or hopeless",
  "Trouble falling or staying asleep, or sleeping too much",
  "Feeling tired or having little energy",
  "Poor appetite or overeating",
  "Feeling bad about yourself – or that you are a failure or have let yourself or your family down",
  "Adults: Trouble concentrating on things, such as reading the newspaper or watching television\nYouth: Trouble concentrating on things like school work, reading, or watching TV",
  "Moving or speaking so slowly that other people could have noticed. Or the opposite – being so fidgety or restless that you have been moving around a lot more than usual",
  "Thoughts that you would be better off dead, or of hurting yourself",
];

const GAD7_QUESTIONS = [
  "Feeling nervous, anxious, or on edge",
  "Not being able to stop or control worrying",
  "Worrying too much about different things",
  "Trouble relaxing",
  "Being so restless that it is hard to sit still",
  "Becoming easily annoyed or irritable",
  "Feeling afraid as if something awful might happen",
];

const SMFQ_QUESTIONS = [
  "S/he felt miserable or unhappy",
  "S/he didn't enjoy anything at all",
  "S/he felt so tired s/he just sat around and did nothing",
  "S/he was very restless",
  "S/he felt s/he was no good anymore",
  "S/he cried a lot",
  "S/he found it hard to think properly or concentrate",
  "S/he hated him/herself",
  "S/he felt s/he was a bad person",
  "S/he felt lonely",
  "S/he thought nobody really loved him/her",
  "S/he thought s/he could never be as good as other kids",
  "S/he did everything wrong",
];

const SCARED_QUESTIONS = [
  "When I feel frightened, it is hard to breathe",
  "I get headaches when I am at school",
  "I don't like to be with people I don't know well",
  "I get scared if I sleep away from home",
  "I worry about other people liking me",
  "When I get frightened, I feel like passing out",
  "I am nervous",
  "I follow my mother or father wherever they go",
  "People tell me that I look nervous",
  "I feel nervous with people I don't know well",
  "I get stomachaches at school",
  "When I get frightened, I feel like I am going crazy",
  "I worry about sleeping alone",
  "I worry about being as good as other kids",
  "When I get frightened, I feel like things are not real",
  "I have nightmares about something bad happening to my parents",
  "I worry about going to school",
  "When I get frightened, my heart beats fast",
  "I get shaky",
  "I have nightmares about something bad happening to me",
  "I worry about things working out for me",
  "When I get frightened, I sweat a lot",
  "I am a worrier",
  "I get really frightened for no reason at all",
  "I am afraid to be alone in the house",
  "It is hard for me to talk with people I don't know well",
  "When I get frightened, I feel like I am choking",
  "People tell me that I worry too much",
  "I don't like to be away from my family",
  "I am afraid of having anxiety (or panic) attacks",
  "I worry that something bad might happen to my parents",
  "I feel shy with people I don't know well",
  "I worry about what is going to happen in the future",
  "When I get frightened, I feel like throwing up",
  "I worry about how well I do things",
  "I am scared to go to school",
  "I worry about things that have already happened",
  "When I get frightened, I feel dizzy",
  "I feel nervous when I am with other children or adults and I have to do something while they watch me",
  "I feel nervous when I am going to parties, dances, or any place where there will be people that I don't know well",
  "I am shy",
];

const PHQ_GAD_OPTIONS = [
  "Not at all",
  "Several days",
  "More than half the days",
  "Nearly every day",
];

const SMFQ_OPTIONS = ["Not True", "Sometimes True", "True"];

const SCARED_OPTIONS = [
  "Not True or Hardly Ever True",
  "Somewhat True or Sometimes True",
  "Very True or Often True",
];

const getQuestionsForType = (type) => {
  if (type === "PHQ-9") return PHQ9_QUESTIONS;
  if (type === "GAD-7") return GAD7_QUESTIONS;
  if (type === "SMFQ") return SMFQ_QUESTIONS;
  if (type === "SCARED") return SCARED_QUESTIONS;
  return [];
};

const getOptionsForType = (type) => {
  if (type === "SMFQ") return SMFQ_OPTIONS;
  if (type === "SCARED") return SCARED_OPTIONS;
  if (type === "PHQ-9" || type === "GAD-7") return PHQ_GAD_OPTIONS;
  return PHQ_GAD_OPTIONS;
};

const usesPillButtons = (type) => type === "SMFQ" || type === "SCARED";

const getScoreFooterLabel = (type) =>
  type === "PHQ-9" ? "Calculated Score" : "Total Score";

/* ─── Severity calculators ─── */
const getSeverity = (type, score) => {
  const num = parseInt(score, 10);
  if (isNaN(num)) return "";
  if (type === "PHQ-9") {
    if (num <= 4) return "Minimal";
    if (num <= 9) return "Mild";
    if (num <= 14) return "Moderate";
    if (num <= 19) return "Moderately Severe";
    return "Severe";
  }
  if (type === "GAD-7") {
    if (num <= 4) return "Minimal Anxiety";
    if (num <= 9) return "Mild Anxiety";
    if (num <= 14) return "Moderate Anxiety";
    return "Severe Anxiety";
  }
  if (type === "SMFQ") {
    if (num <= 7) return "Minimal";
    if (num <= 11) return "Mild";
    if (num <= 19) return "Moderate";
    return "Severe";
  }
  if (type === "SCARED") {
    if (num <= 24) return "Minimal";
    if (num <= 29) return "Mild";
    if (num <= 40) return "Moderate";
    return "Severe";
  }
  return "";
};

/* ─── Date Helpers ─── */
const todayYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const formatDisplayDate = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${m}/${d}/${y}`;
};

/* ─── DateInput Component ─── */
const DateInput = ({ value, onChange, placeholder }) => {
  const ref = useRef(null);
  return (
    <div
      style={{ position: "relative", display: "flex", alignItems: "center" }}
      onClick={() => {
        try {
          ref.current?.showPicker();
        } catch {
          ref.current?.focus();
        }
      }}
    >
      <input
        ref={ref}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          position: "absolute",
          opacity: 0,
          width: "100%",
          height: "100%",
          cursor: "pointer",
          zIndex: 2,
        }}
      />
      <div
        style={{
          border: "1px solid #D1D5DB",
          borderRadius: "8px",
          padding: "10px 14px",
          fontSize: "14px",
          color: value ? "#1F2937" : "#9CA3AF",
          backgroundColor: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          width: "100%",
          minWidth: "160px",
          userSelect: "none",
        }}
      >
        <span>
          {value ? formatDisplayDate(value) : placeholder || "MM/DD/YYYY"}
        </span>
        <FaCalendarAlt
          size={13}
          style={{ color: "#9CA3AF", marginLeft: "8px" }}
        />
      </div>
    </div>
  );
};

/* ─── Patient Search Dropdown ─── */
const PatientSearchDropdown = ({ patients, value, onChange }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = patients.find((p) => p.id === value);
  const filtered = patients.filter(
    (p) =>
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      (p.dob && p.dob.includes(query)),
  );

  useEffect(() => {
    const handleClick = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        style={{
          border: "1px solid #D1D5DB",
          borderRadius: "8px",
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
        }}
      >
        <input
          type="text"
          placeholder="Type to fill"
          value={
            open
              ? query
              : selected
                ? `${selected.name}${selected.dob ? ` (${selected.dob})` : ""}`
                : ""
          }
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
            setQuery("");
          }}
          style={{
            width: "100%",
            border: "none",
            outline: "none",
            padding: "10px 14px",
            fontSize: "14px",
            color: "#1F2937",
            backgroundColor: "transparent",
          }}
        />
      </div>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            zIndex: 999,
            maxHeight: "180px",
            overflowY: "auto",
          }}
        >
          {filtered.length === 0 ? (
            <div
              style={{
                padding: "12px 14px",
                fontSize: "13px",
                color: "#9CA3AF",
              }}
            >
              No patients found
            </div>
          ) : (
            filtered.map((p) => (
              <div
                key={p.id}
                onClick={() => {
                  onChange(p.id);
                  setOpen(false);
                  setQuery("");
                }}
                style={{
                  padding: "10px 14px",
                  fontSize: "13px",
                  cursor: "pointer",
                  color: "#374151",
                  backgroundColor: value === p.id ? "#F0FDF4" : "#FFFFFF",
                  borderBottom: "1px solid #F3F4F6",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#F9FAFB")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    value === p.id ? "#F0FDF4" : "#FFFFFF")
                }
              >
                {p.name}
                {p.dob ? ` (${p.dob})` : ""}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const getEntryMethodLabel = (method) =>
  method === "questionnaire" ? "Questionnaire" : "Score Only";

const getMaxScoreForType = (type) => {
  if (type === "PHQ-9") return 27;
  if (type === "GAD-7") return 21;
  if (type === "SMFQ") return 26;
  if (type === "SCARED") return 82;
  return 999;
};

const computeDisplayScore = (assessment) => {
  if (assessment.method === "questionnaire") {
    const answers = assessment.answers || [];
    const hasAny = answers.some((a) => a !== null && a !== undefined);
    if (!hasAny) return null;
    return answers.reduce(
      (sum, a) => sum + (a !== null && a !== undefined ? a : 0),
      0,
    );
  }
  if (assessment.score === "" || assessment.score === undefined) return null;
  const num = parseInt(assessment.score, 10);
  return isNaN(num) ? null : num;
};

const computeFinalScore = (assessment) => {
  if (assessment.method === "questionnaire") {
    const answers = assessment.answers || [];
    if (answers.some((a) => a === null || a === undefined)) return null;
    return answers.reduce((sum, a) => sum + a, 0);
  }
  return computeDisplayScore(assessment);
};

const summaryActionBtnStyle = {
  backgroundColor: "#FFFFFF",
  border: "1px solid #E5E7EB",
  borderRadius: "6px",
  padding: "6px 14px",
  fontSize: "13px",
  fontWeight: 500,
  cursor: "pointer",
  color: "#374151",
  fontFamily: "Poppins, sans-serif",
  lineHeight: 1.2,
};

const isAssessmentComplete = (assessment) =>
  computeFinalScore(assessment) !== null;

const hasAnySelection = (assessment) => {
  if (assessment.method === "questionnaire") {
    return (assessment.answers || []).some(
      (a) => a !== null && a !== undefined,
    );
  }
  return (
    assessment.score !== "" &&
    assessment.score !== undefined &&
    assessment.score !== null
  );
};

/* ─── Assessment Card (PHQ-9 + other types) ─── */
const AssessmentCard = ({ assessment, activityDate, onUpdate, onRemove }) => {
  const {
    id,
    type,
    method,
    answers = [],
    score,
    expanded = true,
    locked = false,
  } = assessment;
  const questions = getQuestionsForType(type);
  const options = getOptionsForType(type);
  const maxScore = getMaxScoreForType(type);

  const allAnswered =
    method === "questionnaire"
      ? answers.filter((a) => a !== null && a !== undefined).length ===
        questions.length
      : score !== "" && score !== undefined && score !== null;

  const anySelected = hasAnySelection(assessment);
  const totalScore = computeDisplayScore(assessment);
  const finalScore = computeFinalScore(assessment);
  const severity =
    totalScore !== null && !isNaN(totalScore)
      ? getSeverity(type, totalScore)
      : "";
  const summarySeverity =
    finalScore !== null ? getSeverity(type, finalScore) : severity;

  const showSummaryActions = allAnswered || locked;
  const pillStyle = usesPillButtons(type);
  const scoreFooterLabel = getScoreFooterLabel(type);

  const handleAnswerSelect = (qIdx, optIdx) => {
    const newAnswers = [...answers];
    newAnswers[qIdx] = optIdx;
    onUpdate(id, { answers: newAnswers, assessmentDate: activityDate });
  };

  const handleScoreChange = (val) => {
    if (val === "") {
      onUpdate(id, { score: "", assessmentDate: activityDate });
      return;
    }
    const num = parseInt(val, 10);
    if (isNaN(num) || num < 0) return;
    onUpdate(id, {
      score: String(Math.min(num, maxScore)),
      assessmentDate: activityDate,
    });
  };

  const handleCollapse = () => onUpdate(id, { expanded: false });
  const handleExpand = () => onUpdate(id, { expanded: true });
  const handleEdit = () => onUpdate(id, { expanded: true });

  /* ── Collapsed summary with Expand / Edit / Remove ── */
  if (!expanded && showSummaryActions) {
    return (
      <div className="assessment-summary-card">
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#1F2937",
                marginBottom: "12px",
              }}
            >
              {type}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                fontSize: "14px",
                color: "#374151",
              }}
            >
              <div>
                Score:{" "}
                <strong style={{ color: "#1F2937" }}>
                  {finalScore !== null ? finalScore : "—"}
                </strong>
              </div>
              <div>
                Severity:{" "}
                <strong style={{ color: "#1F2937" }}>
                  {summarySeverity || "—"}
                </strong>
              </div>
              <div>
                Entry Method:{" "}
                <strong style={{ color: "#1F2937" }}>
                  {getEntryMethodLabel(method)}
                </strong>
              </div>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              flexShrink: 0,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={handleExpand}
              style={summaryActionBtnStyle}
            >
              Expand
            </button>
            <button
              type="button"
              onClick={handleEdit}
              style={summaryActionBtnStyle}
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => onRemove(id)}
              style={{ ...summaryActionBtnStyle, color: "#EF4444" }}
            >
              Remove
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Collapsed partial (incomplete, not locked) — no action links ── */
  if (!expanded && !showSummaryActions) {
    return (
      <div
        style={{
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          backgroundColor: "#FFFFFF",
          marginBottom: "16px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
          }}
        >
          <span
            style={{ fontSize: "16px", fontWeight: "700", color: "#1F2937" }}
          >
            {type}
          </span>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <button
              type="button"
              onClick={handleExpand}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#6B7280",
                padding: "4px",
                display: "flex",
                alignItems: "center",
              }}
              title="Expand"
            >
              <FaChevronUp size={14} style={{ transform: "rotate(180deg)" }} />
            </button>
            <button
              type="button"
              onClick={() => onRemove(id)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#EF4444",
                padding: "4px",
                display: "flex",
                alignItems: "center",
              }}
              title="Remove"
            >
              <FaTimes size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Expanded view ── */
  return (
    <div
      style={{
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        backgroundColor: "#FFFFFF",
        marginBottom: "16px",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          borderBottom: "1px solid #F3F4F6",
        }}
      >
        <span style={{ fontSize: "16px", fontWeight: "700", color: "#1F2937" }}>
          {type}
        </span>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {anySelected && (
            <button
              type="button"
              onClick={handleCollapse}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#6B7280",
                padding: "4px",
                display: "flex",
                alignItems: "center",
              }}
              title="Collapse"
            >
              <FaChevronUp size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={() => onRemove(id)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#EF4444",
              padding: "4px",
              display: "flex",
              alignItems: "center",
            }}
            title="Remove Assessment"
          >
            <FaTimes size={14} />
          </button>
        </div>
      </div>

      <div style={{ padding: "20px 24px" }}>
        {method === "questionnaire" ? (
          <>
            {questions.map((q, qi) => {
              const selected = answers[qi];
              return (
                <div key={qi} style={{ marginBottom: "28px" }}>
                  <p
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#1F2937",
                      marginBottom: "12px",
                      lineHeight: "1.5",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {qi + 1}. {q}
                  </p>
                  <div className={`assessment-answer-row cols-${options.length}`}>
                    {options.map((opt, oi) => {
                      const isSelected = selected === oi;
                      return (
                        <button
                          key={oi}
                          type="button"
                          onClick={() => handleAnswerSelect(qi, oi)}
                          className={`assessment-answer-btn${pillStyle ? " pill" : ""}${type === "SCARED" ? " scared-opt" : ""}`}
                          style={{
                            padding: pillStyle ? "12px 16px" : "12px 10px",
                            borderRadius: pillStyle ? "9999px" : "8px",
                            border: isSelected
                              ? "2px solid #27B99A"
                              : pillStyle
                                ? "none"
                                : "1px solid #E5E7EB",
                            backgroundColor: isSelected ? "#27B99A" : "#F3F4F6",
                            color: isSelected ? "#FFFFFF" : "#374151",
                            fontSize: type === "SCARED" ? "11px" : "13px",
                            fontWeight: isSelected ? "600" : "400",
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                            textAlign: "center",
                            lineHeight: "1.35",
                          }}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {anySelected && (
              <div className="phq9-score-footer">
                <div className="phq9-score-card">
                  <div className="phq9-score-label">{scoreFooterLabel}</div>
                  <div className="phq9-score-value">
                    {totalScore !== null ? totalScore : "—"}
                  </div>
                </div>
                <div className="phq9-score-card">
                  <div className="phq9-score-label">Severity</div>
                  <div className="phq9-score-value phq9-severity-value">
                    {severity || "—"}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="score-only-fields">
            <div className="score-only-field">
              <label className="score-only-label">Score</label>
              <input
                type="number"
                min="0"
                max={maxScore}
                value={score || ""}
                onChange={(e) => handleScoreChange(e.target.value)}
                placeholder=""
                className="score-only-box ia-input"
              />
            </div>
            <div className="score-only-field">
              <label className="score-only-label">Severity</label>
              <div
                className="score-only-box score-only-display"
                style={{ fontWeight: severity ? 700 : 400 }}
              >
                {severity || ""}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Modal Component ─── */
const Modal = ({ show, onClose, title, children }) => {
  if (!show) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.4)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        animation: "fadeIn 0.15s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          width: "460px",
          maxWidth: "90vw",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          animation: "slideUp 0.2s ease",
          overflow: "hidden",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            backgroundColor: "#0A3652",
            padding: "18px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "16px",
              fontWeight: "700",
              color: "#FFFFFF",
            }}
          >
            {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#FFFFFF",
              padding: "2px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <FaTimes size={16} />
          </button>
        </div>
        {/* Modal Body */}
        <div>{children}</div>
      </div>
    </div>
  );
};

/* ─── Page mode config ─── */
const PAGE_CONFIG = {
  initialAssessment: {
    title: "Initial Assessment",
    activityType: "Initial Assessment",
    auditComment: "Initial assessment completed by care manager.",
    requiresInitialAssessment: false,
  },
  followUp: {
    title: "Follow-Up",
    activityType: "Follow-Up",
    auditComment: "Follow-up completed by care manager.",
    requiresInitialAssessment: true,
  },
};

const patientHasInitialAssessment = (patient, activitiesList = []) => {
  if (patient.initialAssessment) return true;
  return activitiesList.some(
    (a) =>
      a.patientId === patient.id && a.activityType === "Initial Assessment",
  );
};

/* ─── Main CoCM Assessment Activity Page ─── */
const CoCMAssessmentActivityPage = ({ mode = "initialAssessment" }) => {
  const config = PAGE_CONFIG[mode] || PAGE_CONFIG.initialAssessment;
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state?.user);

  /* Patient data */
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [date, setDate] = useState(todayYMD());
  const [duration, setDuration] = useState("50");

  /* Assessments */
  const [assessments, setAssessments] = useState([]);

  /* Notes */
  const [notes, setNotes] = useState("");

  /* Next Follow-Up */
  const [nextFollowUp, setNextFollowUp] = useState(todayYMD());

  /* Modal states */
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showMethodModal, setShowMethodModal] = useState(false);
  const [pendingType, setPendingType] = useState("");

  /* Saving state */
  const [saving, setSaving] = useState(false);

  /* Fetch patients */
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const token = localStorage.getItem("user_id");
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/users/get-patients`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (res.data?.success) {
          let mapped = res.data.data.map((u) => ({
            id: u._id,
            name: `${u.firstname} ${u.lastname}`,
            dob: u.dob
              ? (() => {
                  const d = new Date(u.dob);
                  if (isNaN(d.getTime())) return u.dob;
                  return `${String(d.getUTCMonth() + 1).padStart(2, "0")}/${String(d.getUTCDate()).padStart(2, "0")}/${d.getUTCFullYear()}`;
                })()
              : "",
            initialAssessment: u.cocmMeta?.initialAssessment || "",
          }));

          if (config.requiresInitialAssessment) {
            const actRes = await axios.get(
              `${process.env.REACT_APP_BASE_URL}/cocm-activities`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            const activities = actRes.data?.data || [];
            mapped = mapped.filter((p) =>
              patientHasInitialAssessment(p, activities),
            );
          }

          setPatients(mapped);
        }
      } catch (e) {
        console.error("Failed to fetch patients:", e);
      }
    };
    fetchPatients();
  }, [config.requiresInitialAssessment]);

  useEffect(() => {
    const {
      patientId: preselectedId,
      openAssessmentModal,
      autoAssessment,
    } = location.state || {};
    if (preselectedId) {
      setPatientId(preselectedId);
    }
    if (openAssessmentModal) {
      setShowTypeModal(true);
    }
    if (autoAssessment?.type && autoAssessment?.method) {
      const questions = getQuestionsForType(autoAssessment.type);
      setAssessments((prev) => [
        ...prev.map((a) => ({ ...a, locked: true, expanded: false })),
        {
          id: `${autoAssessment.type}-${Date.now()}`,
          type: autoAssessment.type,
          method:
            autoAssessment.method === "questionnaire"
              ? "questionnaire"
              : "score",
          answers: new Array(questions.length).fill(null),
          score: "",
          expanded: true,
          locked: false,
          assessmentDate: date,
        },
      ]);
    }
  }, [location.state]);

  /* ─── Assessment handlers ─── */
  const handleAddAssessmentClick = () => setShowTypeModal(true);

  const handleSelectType = (type) => {
    setShowTypeModal(false);
    setPendingType(type);
    setShowMethodModal(true);
  };

  const handleSelectMethod = (method) => {
    setShowMethodModal(false);
    const questions = getQuestionsForType(pendingType);
    setAssessments((prev) => [
      ...prev.map((a) => ({ ...a, locked: true, expanded: false })),
      {
        id: `${pendingType}-${Date.now()}`,
        type: pendingType,
        method: method === "questionnaire" ? "questionnaire" : "score",
        answers: new Array(questions.length).fill(null),
        score: "",
        expanded: true,
        locked: false,
        assessmentDate: date,
      },
    ]);
    setPendingType("");
  };

  const handleUpdateAssessment = (id, updates) => {
    setAssessments((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...updates } : a)),
    );
  };

  const handleRemoveAssessment = (id) => {
    setAssessments((prev) => prev.filter((a) => a.id !== id));
  };

  useEffect(() => {
    setAssessments((prev) =>
      prev.map((a) => ({ ...a, assessmentDate: date })),
    );
  }, [date]);

  /* ─── Save handler ─── */
  const handleSave = async () => {
    if (!patientId) {
      alert("Please select a patient.");
      return;
    }

    const incomplete = assessments.filter((a) => !isAssessmentComplete(a));
    if (incomplete.length > 0) {
      alert("Please complete all assessments before submitting.");
      return;
    }

    if (assessments.length === 0) {
      alert("Please add at least one assessment.");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("user_id");

      const assessmentPayload = assessments.map((a) => {
        const calcScore = computeFinalScore(a) ?? 0;
        return {
          type: a.type,
          method: a.method,
          entryMethod: getEntryMethodLabel(a.method),
          score: calcScore,
          severity: getSeverity(a.type, calcScore),
          answers: a.answers,
          date: a.assessmentDate || date,
        };
      });

      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/cocm-activities`,
        {
          patientId,
          activityType: config.activityType,
          activityDate: date,
          duration: parseInt(duration, 10) || 0,
          notes,
          assessments: assessmentPayload,
          nextFollowUpDate: nextFollowUp,
          auditComment: config.auditComment,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert(`${config.title} saved successfully.`);
      navigate("/activity-cocm");
    } catch (e) {
      console.error("Failed to save:", e);
      alert("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const ASSESSMENT_TYPES = ["PHQ-9", "GAD-7", "SMFQ", "SCARED"];

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "90%",
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                .ia-input:focus { border-color: #27B99A !important; box-shadow: 0 0 0 3px rgba(39,185,154,0.15) !important; outline: none; }
                .ia-textarea:focus { border-color: #27B99A !important; box-shadow: 0 0 0 3px rgba(39,185,154,0.15) !important; outline: none; }
                .method-card:hover { border-color: #27B99A !important; background-color: #F0FDF4 !important; }
                .type-item:hover { background-color: #F9FAFB !important; }
                .patient-info-grid {
                    display: grid;
                    grid-template-columns: 2fr 1fr 1fr;
                    gap: 20px;
                    align-items: end;
                }
                @media (max-width: 768px) {
                    .patient-info-grid {
                        grid-template-columns: 1fr;
                    }
                }
                .phq9-answer-row,
                .assessment-answer-row {
                    display: grid;
                    gap: 8px;
                }
                .assessment-answer-row.cols-4,
                .phq9-answer-row {
                    grid-template-columns: repeat(4, 1fr);
                }
                .assessment-answer-row.cols-3 {
                    grid-template-columns: repeat(3, 1fr);
                }
                @media (max-width: 900px) {
                    .assessment-answer-row.cols-4,
                    .phq9-answer-row {
                        grid-template-columns: repeat(2, 1fr);
                    }
                    .assessment-answer-row.cols-3 {
                        grid-template-columns: 1fr;
                    }
                }
                @media (min-width: 901px) and (max-width: 1100px) {
                    .assessment-answer-row.cols-3.scared-row {
                        grid-template-columns: 1fr;
                    }
                }
                .phq9-score-footer {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                    margin-top: 8px;
                }
                .phq9-score-card {
                    flex: 1;
                    padding: 28px 24px;
                    background-color: #F9FAFB;
                    border-radius: 10px;
                    border: 1px solid #F3F4F6;
                    text-align: center;
                }
                .phq9-score-label {
                    font-size: 13px;
                    color: #6B7280;
                    font-weight: 500;
                    margin-bottom: 10px;
                }
                .phq9-score-value {
                    font-size: 32px;
                    font-weight: 700;
                    color: #1F2937;
                    line-height: 1;
                }
                .phq9-severity-value {
                    font-size: 22px;
                    line-height: 1.2;
                }
                @media (max-width: 600px) {
                    .phq9-score-footer {
                        grid-template-columns: 1fr;
                    }
                }
                .assessment-summary-card {
                    border: 1px solid #E2E8F0;
                    border-radius: 12px;
                    background-color: #EFF4F8;
                    margin-bottom: 16px;
                    padding: 20px 24px;
                }
                .score-only-fields {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }
                .score-only-field {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .score-only-label {
                    font-size: 13px;
                    font-weight: 500;
                    color: #6B7280;
                    margin: 0;
                }
                .score-only-box {
                    width: 100%;
                    border: none;
                    border-radius: 8px;
                    padding: 14px 16px;
                    font-size: 15px;
                    color: #1F2937;
                    background-color: #F3F4F6;
                    box-sizing: border-box;
                }
                .score-only-box.ia-input:focus {
                    box-shadow: 0 0 0 2px rgba(39, 185, 154, 0.25) !important;
                    outline: none;
                }
                .score-only-display {
                    min-height: 48px;
                    display: flex;
                    align-items: center;
                }
            `}</style>

      {/* Page Content */}
      <div
        style={{
          maxWidth: "100%",
          width: "100%",
          margin: "0 auto",
          padding: "24px 20px 24px 20px",
        }}
      >
        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#27B99A",
            fontWeight: "600",
            fontSize: "14px",
            marginBottom: "16px",
            padding: "6px 0",
          }}
        >
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              backgroundColor: "#27B99A",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaChevronLeft size={11} color="#FFFFFF" />
          </div>
          Back
        </button>

        {/* Page Title */}
        <h1
          style={{
            fontSize: "22px",
            fontWeight: "700",
            color: "#1F2937",
            marginBottom: "24px",
          }}
        >
          {config.title}
        </h1>

        {/* ── Patient Information Card ── */}
        <div style={cardStyle}>
          <h2 style={sectionTitleStyle}>Patient Information</h2>
          <div className="patient-info-grid">
            <div>
              <label style={labelStyle}>Patient Name</label>
              <PatientSearchDropdown
                patients={patients}
                value={patientId}
                onChange={setPatientId}
              />
              {config.requiresInitialAssessment && patients.length === 0 && (
                <p
                  style={{
                    margin: "8px 0 0",
                    fontSize: "13px",
                    color: "#9CA3AF",
                  }}
                >
                  No patients with a completed Initial Assessment yet.
                </p>
              )}
            </div>
            <div>
              <label style={labelStyle}>Date</label>
              <DateInput value={date} onChange={setDate} />
            </div>
            <div>
              <label style={labelStyle}>Duration (minutes)</label>
              <input
                type="number"
                min="1"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="ia-input"
                style={{
                  width: "100%",
                  border: "1px solid #D1D5DB",
                  borderRadius: "8px",
                  padding: "10px 14px",
                  fontSize: "14px",
                  color: "#1F2937",
                  transition: "border-color 0.15s",
                }}
              />
            </div>
          </div>
        </div>

        {/* ── Assessments Card ── */}
        <div style={cardStyle}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "20px",
            }}
          >
            <h2 style={{ ...sectionTitleStyle, marginBottom: 0 }}>
              Assessments
            </h2>
            <button
              onClick={handleAddAssessmentClick}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                backgroundColor: "#27B99A",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "8px",
                padding: "10px 18px",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background-color 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#1FA885")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#27B99A")
              }
            >
              <FaPlus size={11} />
              Add Assessment
            </button>
          </div>

          {assessments.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "48px 20px",
                color: "#9CA3AF",
                fontSize: "14px",
              }}
            >
              <p
                style={{
                  marginBottom: "4px",
                  fontWeight: "500",
                  color: "#6B7280",
                }}
              >
                No assessments added yet
              </p>
              <p style={{ margin: 0, fontSize: "13px" }}>
                Click "Add Assessment" to begin
              </p>
            </div>
          ) : (
            assessments.map((a) => (
              <AssessmentCard
                key={a.id}
                assessment={a}
                activityDate={date}
                onUpdate={handleUpdateAssessment}
                onRemove={handleRemoveAssessment}
              />
            ))
          )}
        </div>

        {/* ── Notes Card ── */}
        <div style={cardStyle}>
          <h2 style={sectionTitleStyle}>Notes</h2>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Free-text"
            className="ia-textarea"
            rows={4}
            style={{
              width: "100%",
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
              padding: "12px 14px",
              fontSize: "14px",
              color: "#1F2937",
              resize: "vertical",
              fontFamily: "Poppins, sans-serif",
              transition: "border-color 0.15s",
            }}
          />
        </div>

        {/* ── Next Follow-Up Card ── */}
        <div style={{ ...cardStyle, border: "2px solid #2F6FED" }}>
          <h2 style={sectionTitleStyle}>Next Follow-Up</h2>
          <div>
            <label style={labelStyle}>Next Follow-Up Date</label>
            <DateInput value={nextFollowUp} onChange={setNextFollowUp} />
          </div>
        </div>

        <div
          style={{
            bottom: 0,
            left: 0,
            right: 0,
            display: "flex",
            gap: "12px",
          }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              flex: 1,
              padding: "14px",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              backgroundColor: "#F9FAFB",
              color: "#374151",
              fontSize: "15px",
              fontWeight: "600",
              cursor: "pointer",
              fontFamily: "Poppins, sans-serif",
              transition: "background-color 0.15s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#F3F4F6")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#F9FAFB")
            }
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1,
              padding: "14px",
              border: "none",
              borderRadius: "8px",
              backgroundColor: saving ? "#9CA3AF" : "#27B99A",
              color: "#FFFFFF",
              fontSize: "15px",
              fontWeight: "600",
              cursor: saving ? "not-allowed" : "pointer",
              fontFamily: "Poppins, sans-serif",
              transition: "background-color 0.15s",
            }}
            onMouseEnter={(e) => {
              if (!saving) e.currentTarget.style.backgroundColor = "#1FA885";
            }}
            onMouseLeave={(e) => {
              if (!saving) e.currentTarget.style.backgroundColor = "#27B99A";
            }}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* ── Select Assessment Type Modal ── */}
      <Modal
        show={showTypeModal}
        onClose={() => setShowTypeModal(false)}
        title="Select Assessment Type"
      >
        <div style={{ padding: "8px 0" }}>
          {ASSESSMENT_TYPES.map((type) => (
            <div
              key={type}
              className="type-item"
              onClick={() => handleSelectType(type)}
              style={{
                padding: "16px 24px",
                fontSize: "15px",
                fontWeight: "500",
                color: "#1F2937",
                cursor: "pointer",
                borderBottom: "1px solid #F3F4F6",
                transition: "background-color 0.1s",
              }}
            >
              {type}
            </div>
          ))}
        </div>
      </Modal>

      {/* ── Select Entry Method Modal ── */}
      <Modal
        show={showMethodModal}
        onClose={() => setShowMethodModal(false)}
        title="Select Entry Method"
      >
        <div style={{ padding: "16px" }}>
          <div
            className="method-card"
            onClick={() => handleSelectMethod("questionnaire")}
            style={{
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              padding: "18px 20px",
              cursor: "pointer",
              marginBottom: "12px",
              transition: "all 0.15s",
            }}
          >
            <div
              style={{
                fontWeight: "600",
                fontSize: "15px",
                color: "#1F2937",
                marginBottom: "4px",
              }}
            >
              Complete Questionnaire
            </div>
            <div style={{ fontSize: "13px", color: "#6B7280" }}>
              Answer all assessment questions manually
            </div>
          </div>
          <div
            className="method-card"
            onClick={() => handleSelectMethod("score")}
            style={{
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              padding: "18px 20px",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            <div
              style={{
                fontWeight: "600",
                fontSize: "15px",
                color: "#1F2937",
                marginBottom: "4px",
              }}
            >
              Enter Score Only
            </div>
            <div style={{ fontSize: "13px", color: "#6B7280" }}>
              Use when assessment was self-administered or completed elsewhere
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

/* ─── Shared Styles ─── */
const cardStyle = {
  backgroundColor: "#FFFFFF",
  border: "1px solid #E5E7EB",
  borderRadius: "12px",
  padding: "24px",
  marginBottom: "20px",
};

const sectionTitleStyle = {
  fontSize: "15px",
  fontWeight: "600",
  color: "#1F2937",
  marginBottom: "16px",
};

const labelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: "500",
  color: "#374151",
  marginBottom: "6px",
};

const InitialAssessmentPage = () => (
  <CoCMAssessmentActivityPage mode="initialAssessment" />
);

const FollowUpPage = () => <CoCMAssessmentActivityPage mode="followUp" />;

export default InitialAssessmentPage;
export { FollowUpPage };
